// TanStack Query setup for hub-app.
//
// Single QueryClient lives at the app root. Provided to the tree via
// `QueryClientProvider` in main.ts; consumers call `createQuery({...})`
// or `createMutation({...})` from `@tanstack/svelte-query` inside
// components.
//
// Defaults are tuned for the ops-console use case: aggressive
// reuse (long stale times) so navigating between views doesn't
// re-hit the network for data we just fetched, but no surprise
// refetches on tab focus (would re-stream LLM tokens in the chat
// view, etc.).

import { QueryClient } from "@tanstack/svelte-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Treat data as fresh for 30 s. Components mounting within
      // this window get the cached value with no network round-trip.
      // Real-time freshness comes from SSE → invalidateQueries (see
      // src/api/sse.ts), not from polling defaults.
      staleTime: 30_000,
      // Garbage-collect cached results 5 min after the last mount.
      gcTime: 5 * 60_000,
      // No automatic refetch on tab-focus. Customer flow: an admin
      // alt-tabs to Outlook, back to the Hub — refetching every
      // open query would feel like a freeze. Explicit refetch
      // mechanisms (SSE invalidation, manual refresh button) cover
      // the actual freshness needs.
      refetchOnWindowFocus: false,
      // Retry once on network/5xx; treat 4xx as terminal (auth /
      // not-found / validation errors won't get better on retry).
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        // openapi-fetch error shape: { response: Response, ... }.
        // 4xx — don't retry.
        const status = (error as { response?: Response })?.response?.status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return true;
      },
    },
    mutations: {
      // Mutations don't auto-retry — a duplicate POST is rarely
      // safe without an idempotency key.
      retry: false,
    },
  },
});
